/**
 * Step 3 — Registered Vehicles
 *
 * Mirrors Step 2's pattern: upload (multi-file) → preview → confirm.
 *
 * The audit service's /v1/preview parses vehicle files and handles column
 * detection on its end; the dispatch UI does not need a column mapping
 * modal. (An earlier version opened ColumnMappingModal on every parse,
 * which crashed because the audit service doesn't return headers/auto_mapping
 * fields. Matt hit that.)
 *
 * Manual-entry escape hatch is the same as Step 2 — when the parser can't
 * read the file, we surface an editable count so the user isn't dead-ended.
 */
import React, { useState } from 'react';
import { api } from '../../../api';

const BRAND = '#3F66A0';
const BRAND_TINT = '#eef3fb';

const cardStyle = {
  background: '#fff',
  borderRadius: 16,
  padding: '28px 32px',
  marginBottom: 20,
  border: '1px solid #e2e8f0',
};

const sectionTitleStyle = {
  margin: 0,
  marginBottom: 4,
  fontSize: 17,
  fontWeight: 700,
  color: '#0f172a',
  letterSpacing: '-0.01em',
};

const sectionHelpStyle = {
  marginTop: 0,
  marginBottom: 22,
  fontSize: 13,
  color: '#64748b',
  lineHeight: 1.5,
};

export default function Step3Vehicles({ draftId, formData, onChange, onNext, onBack, files, refreshFiles }) {
  const [uploading, setUploading] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(formData?.vehicle_summary || null);
  const [parseFailed, setParseFailed] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ total_vehicles: 0, total_units: 0 });

  const existing = (files || []).filter(f => f.file_kind === 'vehicles');

  const triggerParse = async () => {
    setParsing(true);
    setError('');
    setParseFailed(false);
    try {
      const res = await api.parseAuditVehicles(draftId);
      const summary = {
        total_vehicles: res.total_vehicles ?? res.summary?.total_vehicles ?? 0,
        total_units: res.total_units ?? res.summary?.total_units ?? 0,
      };
      if (summary.total_vehicles > 0) {
        setPreview(summary);
        onChange({ ...formData, vehicle_summary: summary });
      } else {
        // Parser returned nothing usable — let the user enter values
        setPreview({ total_vehicles: 0, total_units: 0 });
        setDraft({ total_vehicles: 0, total_units: 0 });
        setParseFailed(true);
        setEditing(true);
        setError("We couldn't detect vehicles in this file. Enter values manually below.");
      }
    } catch (err) {
      setPreview({ total_vehicles: 0, total_units: 0 });
      setDraft({ total_vehicles: 0, total_units: 0 });
      setParseFailed(true);
      setEditing(true);
      setError(`Couldn't auto-read this file (${err.message}). Enter values manually below.`);
    } finally {
      setParsing(false);
    }
  };

  const handleUpload = async (e) => {
    const fl = e.target.files;
    if (!fl || !fl.length) return;
    setUploading(true);
    setError('');
    try {
      await api.uploadAuditFiles(draftId, 'vehicles', Array.from(fl));
      await refreshFiles();
      setUploading(false);
      await triggerParse();
    } catch (err) {
      setError(err.message);
      setUploading(false);
    }
  };

  const handleRemove = async (id) => {
    if (!window.confirm('Remove this file?')) return;
    await api.deleteAuditFile(draftId, id);
    await refreshFiles();
    setPreview(null);
    setParseFailed(false);
    setEditing(false);
  };

  const startManualEntry = () => {
    setPreview({ total_vehicles: 0, total_units: 0 });
    setDraft({ total_vehicles: 0, total_units: 0 });
    setParseFailed(true);
    setEditing(true);
  };

  const saveEdits = () => {
    setPreview({ ...draft });
    onChange({ ...formData, vehicle_summary: { ...draft } });
    setEditing(false);
  };

  const cancelEdits = () => {
    setDraft({ ...preview });
    setEditing(false);
  };

  const reupload = () => {
    setPreview(null);
    setParseFailed(false);
    setEditing(false);
    setError('');
  };

  const canConfirm = preview && !editing && (preview.total_vehicles > 0 || parseFailed);

  return (
    <div className="audit-step audit-step-3">

      {/* ─── Card 1: Upload ─── */}
      <section style={cardStyle}>
        <h2 style={sectionTitleStyle}>Registered Vehicles</h2>
        <p style={sectionHelpStyle}>
          Upload your parking system's vehicle export(s) — CSV or Excel. You can upload
          multiple files; we'll dedupe by unit + license plate. If our parser can't read
          your file, you'll be able to enter the counts manually.
        </p>

        {!preview && (
          <>
            <label style={{
              display: 'block', padding: '32px 24px', borderRadius: 14,
              border: `2px dashed ${uploading || parsing ? BRAND : '#cbd5e1'}`,
              background: uploading || parsing ? BRAND_TINT : '#fafbfc',
              textAlign: 'center', cursor: uploading || parsing ? 'wait' : 'pointer',
              transition: 'all 0.15s',
            }}>
              <input
                type="file"
                accept=".csv,.xlsx,.xls"
                multiple
                onChange={handleUpload}
                disabled={uploading || parsing}
                style={{ display: 'none' }}
              />
              <div style={{ fontSize: 36, marginBottom: 8 }}>🚗</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>
                {uploading ? 'Uploading…' :
                  parsing ? 'Reading vehicle file(s)…' :
                    existing.length > 0 ? 'Add another file' :
                      'Click to upload CSV or Excel'}
              </div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                .csv, .xlsx, or .xls · multiple files OK
              </div>
            </label>

            <div style={{ marginTop: 14, textAlign: 'center' }}>
              <button
                type="button"
                onClick={startManualEntry}
                style={{
                  padding: 0, background: 'none', border: 'none',
                  color: BRAND, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                }}
              >
                Don't have a file? Enter values manually →
              </button>
            </div>
          </>
        )}

        {existing.length > 0 && (
          <ul style={{
            listStyle: 'none', padding: 0, margin: preview ? 0 : '20px 0 0',
            border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden',
          }}>
            {existing.map((f, i) => (
              <li key={f.id} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '12px 16px',
                borderTop: i === 0 ? 'none' : '1px solid #f1f5f9',
                background: '#fafbfc',
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {f.original_filename}
                  </div>
                  <div style={{ fontSize: 12, color: '#64748b' }}>
                    {Math.round(f.size_bytes / 1024)} KB
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemove(f.id)}
                  style={{
                    padding: '6px 12px', background: 'none', border: '1px solid #fecaca',
                    color: '#b91c1c', borderRadius: 6, fontSize: 12, fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}

        {error && (
          <div style={{
            marginTop: preview ? 0 : 16,
            padding: '12px 16px', borderRadius: 10,
            background: '#fef3c7', border: '1px solid #fcd34d',
            color: '#78350f', fontSize: 13, fontWeight: 600,
          }}>
            {error}
          </div>
        )}
      </section>

      {/* ─── Card 2: Preview / Manual entry ─── */}
      {preview && (
        <section style={cardStyle}>
          <h3 style={{ ...sectionTitleStyle, fontSize: 14, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b' }}>
            Vehicle Summary
          </h3>
          {!editing ? (
            preview.total_vehicles > 0 ? (
              <p style={{ fontSize: 16, color: '#0f172a', margin: '0 0 18px' }}>
                Detected <strong>{preview.total_vehicles}</strong> vehicle{preview.total_vehicles === 1 ? '' : 's'}
                {preview.total_units > 0 ? <> across <strong>{preview.total_units}</strong> unit{preview.total_units === 1 ? '' : 's'}</> : null}.
              </p>
            ) : (
              <p style={{ fontSize: 14, color: '#64748b', margin: '0 0 18px' }}>
                No values detected — enter the vehicle counts below, then confirm.
              </p>
            )
          ) : (
            <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 18px' }}>
              Enter the totals from the property's parking system, then save.
            </p>
          )}

          <div style={{
            display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 18,
          }}>
            <div>
              <label className="sales-label">Total Vehicles</label>
              {editing ? (
                <input
                  type="number" min="0" inputMode="numeric" className="sales-input"
                  value={draft.total_vehicles ?? 0}
                  onChange={(e) => setDraft({ ...draft, total_vehicles: parseInt(e.target.value || 0) })}
                />
              ) : (
                <div style={{ fontSize: 24, fontWeight: 800, color: BRAND }}>{preview.total_vehicles ?? 0}</div>
              )}
            </div>
            <div>
              <label className="sales-label">Unique Units With Vehicles</label>
              {editing ? (
                <input
                  type="number" min="0" inputMode="numeric" className="sales-input"
                  value={draft.total_units ?? 0}
                  onChange={(e) => setDraft({ ...draft, total_units: parseInt(e.target.value || 0) })}
                />
              ) : (
                <div style={{ fontSize: 24, fontWeight: 800, color: BRAND }}>{preview.total_units ?? 0}</div>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            {editing ? (
              <>
                <button
                  type="button" onClick={cancelEdits}
                  style={{ padding: '10px 16px', borderRadius: 8, border: '1.5px solid #e2e8f0', background: '#fff', color: '#475569', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                >Cancel</button>
                <button
                  type="button" onClick={saveEdits}
                  style={{ padding: '10px 16px', borderRadius: 8, border: 'none', background: BRAND, color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
                >Save edits</button>
              </>
            ) : (
              <>
                <button
                  type="button" onClick={() => { setDraft({ ...preview }); setEditing(true); }}
                  style={{ padding: '10px 16px', borderRadius: 8, border: '1.5px solid #e2e8f0', background: '#fff', color: '#475569', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                >Edit values</button>
                <button
                  type="button" onClick={reupload}
                  style={{ padding: '10px 16px', borderRadius: 8, border: '1.5px solid #e2e8f0', background: '#fff', color: '#475569', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                >Re-upload</button>
              </>
            )}
          </div>
        </section>
      )}

      {/* Step actions */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
        <button
          type="button"
          onClick={onBack}
          style={{
            padding: '14px 24px', borderRadius: 12, fontSize: 14, fontWeight: 700,
            border: '1.5px solid #e2e8f0', cursor: 'pointer',
            background: '#fff', color: '#475569',
          }}
        >
          ← Back
        </button>
        {preview && (
          <button
            type="button"
            onClick={onNext}
            disabled={!canConfirm}
            style={{
              padding: '14px 28px', borderRadius: 12, fontSize: 15, fontWeight: 700,
              border: 'none', cursor: canConfirm ? 'pointer' : 'not-allowed',
              background: canConfirm ? BRAND : '#cbd5e1',
              color: '#fff', transition: 'all 0.15s',
              boxShadow: canConfirm ? '0 2px 6px rgba(63,102,160,0.25)' : 'none',
            }}
          >
            {/* "Next: MyVIP" only meaningful when MyVIP step is in play. The
                wizard already routes correctly via step3Next; the button text
                stays generic. */}
            Next →
          </button>
        )}
      </div>
    </div>
  );
}
